import { useMemo } from 'react'

/**
 * Renders a ReactFlow-shaped SOP section — `{ type:'reactflow', nodes:[…],
 * edges:[…] }` — as a real flow diagram (boxes + arrows), instead of dumping
 * the raw JSON. Dependency-free: a hand-rolled SVG that lays nodes out at the
 * positions the diagram already carries and draws edges between box borders.
 *
 * Node shapes by `type`:
 *   • start / end / terminal / startEnd → pill (rounded, filled navy)
 *   • decision / diamond / condition    → diamond
 *   • everything else (process, …)      → rounded rectangle
 *
 * Falls back to a simple top-down auto-layout when the nodes have no usable
 * positions (all identical), so a positionless graph still reads as a flow.
 * All colours are CSS-var tokens, so it works in light + dark.
 */
const NODE_W = 176
const NODE_H = 52
const PAD = 28
const V_GAP = 96 // auto-layout vertical spacing when positions are missing

const isTerminal = (t) => /^(start|end|terminal|start[_-]?end|startend)$/i.test(t ?? '')
const isDecision = (t) => /(decision|diamond|condition|choice)/i.test(t ?? '')

function normalise(value) {
  const rawNodes = Array.isArray(value?.nodes) ? value.nodes : []
  let nodes = rawNodes.map((n, i) => ({
    id: String(n.id ?? i),
    type: n.type ?? 'process',
    label: String(n.data?.label ?? n.label ?? n.id ?? ''),
    x: Number(n.position?.x ?? n.x ?? 0),
    y: Number(n.position?.y ?? n.y ?? 0),
  }))
  const edges = (Array.isArray(value?.edges) ? value.edges : []).map((e, i) => ({
    id: String(e.id ?? i),
    source: String(e.source ?? ''),
    target: String(e.target ?? ''),
    label: String(e.label ?? e.data?.label ?? ''),
  }))

  // No usable positions (all points coincide) → lay out top-down in a simple
  // BFS order from the node(s) with no incoming edge.
  const spreadX = Math.max(...nodes.map((n) => n.x)) - Math.min(...nodes.map((n) => n.x))
  const spreadY = Math.max(...nodes.map((n) => n.y)) - Math.min(...nodes.map((n) => n.y))
  if (nodes.length && spreadX === 0 && spreadY === 0) {
    const incoming = new Set(edges.map((e) => e.target))
    const order = []
    const seen = new Set()
    const queue = nodes.filter((n) => !incoming.has(n.id)).map((n) => n.id)
    if (queue.length === 0 && nodes[0]) queue.push(nodes[0].id)
    const byId = new Map(nodes.map((n) => [n.id, n]))
    while (queue.length) {
      const id = queue.shift()
      if (seen.has(id)) continue
      seen.add(id)
      order.push(id)
      for (const e of edges) if (e.source === id && !seen.has(e.target)) queue.push(e.target)
    }
    for (const n of nodes) if (!seen.has(n.id)) order.push(n.id) // stragglers
    nodes = order.map((id, i) => ({ ...byId.get(id), x: 0, y: i * V_GAP }))
  }
  return { nodes, edges }
}

/** Point on a node's rectangle border along the line toward (tx,ty). */
function borderPoint(cx, cy, hw, hh, tx, ty) {
  const dx = tx - cx
  const dy = ty - cy
  if (dx === 0 && dy === 0) return { x: cx, y: cy }
  const scale = 1 / Math.max(Math.abs(dx) / hw, Math.abs(dy) / hh)
  return { x: cx + dx * scale, y: cy + dy * scale }
}

function truncate(text, max = 24) {
  return text.length > max ? text.slice(0, max - 1) + '…' : text
}

/** SOP labels may carry inline HTML (`<br/>` for line breaks, entities). SVG
 *  `<text>` renders that literally, so normalise: `<br>` → newline, strip any
 *  other tags, decode common entities. */
function cleanLabel(raw) {
  return String(raw ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
}

/**
 * Serialise a ReactFlow diagram section into readable plain text so the AI
 * task generator can "read" the flowchart, not just the SOP prose. Emits the
 * step names plus the connections (arrows), with decision-branch labels — so
 * the model sees the process order and its conditional branches.
 */
export function reactflowToText(value) {
  const nodes = Array.isArray(value?.nodes) ? value.nodes : []
  if (!nodes.length) return ''
  const oneLine = (raw) => cleanLabel(raw).replace(/\s*\n\s*/g, ' ').trim()
  const label = (n) => oneLine(n?.data?.label ?? n?.label ?? n?.id ?? '')
  const byId = new Map(nodes.map((n) => [String(n.id), n]))
  const steps = nodes.map(label).filter(Boolean)
  const flow = (Array.isArray(value?.edges) ? value.edges : [])
    .map((e) => {
      const s = byId.get(String(e.source))
      const t = byId.get(String(e.target))
      if (!s || !t) return ''
      const a = label(s)
      const b = label(t)
      if (!a || !b) return ''
      const lbl = oneLine(e.label ?? e.data?.label ?? '')
      return lbl ? `- ${a} —[${lbl}]→ ${b}` : `- ${a} → ${b}`
    })
    .filter(Boolean)
  const out = []
  if (steps.length) out.push('Process steps:\n' + steps.map((s) => `- ${s}`).join('\n'))
  if (flow.length) {
    out.push(
      'Process flow (arrows show order; bracketed labels are decision outcomes):\n' +
        flow.join('\n'),
    )
  }
  return out.join('\n\n')
}

/** Cleaned label split into display lines (honouring `<br>`), each truncated. */
function labelLines(raw, maxLines = 3, maxChars = 24) {
  const lines = cleanLabel(raw)
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => truncate(s, maxChars))
  return lines.length > maxLines
    ? [...lines.slice(0, maxLines - 1), truncate(lines[maxLines - 1] + '…', maxChars)]
    : lines
}

export function SopFlowDiagram({ value }) {
  const { nodes, edges } = useMemo(() => normalise(value), [value])
  if (!nodes.length) return null

  const minX = Math.min(...nodes.map((n) => n.x))
  const minY = Math.min(...nodes.map((n) => n.y))
  const laid = nodes.map((n) => ({ ...n, x: n.x - minX + PAD, y: n.y - minY + PAD }))
  const byId = new Map(laid.map((n) => [n.id, n]))
  const width = Math.max(...laid.map((n) => n.x + NODE_W)) + PAD
  const height = Math.max(...laid.map((n) => n.y + NODE_H)) + PAD
  const cx = (n) => n.x + NODE_W / 2
  const cy = (n) => n.y + NODE_H / 2

  return (
    <div className="overflow-x-auto rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-3">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        role="img"
        aria-label="SOP process flow diagram"
        className="max-w-full"
        style={{ minWidth: Math.min(width, 280) }}
      >
        <defs>
          <marker id="sop-flow-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="var(--color-ink-subtle)" />
          </marker>
        </defs>

        {/* Edges first, so node boxes paint over the line ends. */}
        {edges.map((e) => {
          const s = byId.get(e.source)
          const t = byId.get(e.target)
          if (!s || !t) return null
          const p1 = borderPoint(cx(s), cy(s), NODE_W / 2, NODE_H / 2, cx(t), cy(t))
          const p2 = borderPoint(cx(t), cy(t), NODE_W / 2, NODE_H / 2, cx(s), cy(s))
          const midX = (p1.x + p2.x) / 2
          const midY = (p1.y + p2.y) / 2
          const elabel = e.label ? truncate(cleanLabel(e.label).replace(/\n/g, ' '), 18) : ''
          return (
            <g key={e.id}>
              <line
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke="var(--color-ink-subtle)"
                strokeWidth="1.5"
                markerEnd="url(#sop-flow-arrow)"
              />
              {elabel ? (
                <g>
                  <rect
                    x={midX - elabel.length * 3.4 - 4}
                    y={midY - 9}
                    width={elabel.length * 6.8 + 8}
                    height="18"
                    rx="4"
                    fill="var(--color-card)"
                    stroke="var(--color-rule)"
                  />
                  <text x={midX} y={midY + 4} textAnchor="middle" fontSize="11" fill="var(--color-ink-muted)">
                    {elabel}
                  </text>
                </g>
              ) : null}
            </g>
          )
        })}

        {/* Nodes */}
        {laid.map((n) => {
          const terminal = isTerminal(n.type)
          const decision = isDecision(n.type)
          const fill = terminal ? 'var(--color-accent-strong)' : 'var(--color-card)'
          const stroke = terminal ? 'var(--color-accent-strong)' : decision ? 'var(--color-accent)' : 'var(--color-rule)'
          const textFill = terminal ? '#ffffff' : 'var(--color-ink-strong)'
          const tcx = cx(n)
          const tcy = cy(n)
          return (
            <g key={n.id}>
              {decision ? (
                <polygon
                  points={`${tcx},${n.y - 6} ${n.x + NODE_W + 6},${tcy} ${tcx},${n.y + NODE_H + 6} ${n.x - 6},${tcy}`}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="1.5"
                />
              ) : (
                <rect
                  x={n.x}
                  y={n.y}
                  width={NODE_W}
                  height={NODE_H}
                  rx={terminal ? NODE_H / 2 : 10}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="1.5"
                />
              )}
              {(() => {
                const lines = labelLines(n.label)
                const lineH = 14
                const startY = tcy + 4 - ((lines.length - 1) * lineH) / 2
                return (
                  <text
                    textAnchor="middle"
                    fontSize="12.5"
                    fontWeight={terminal ? 700 : 500}
                    fill={textFill}
                  >
                    {lines.map((ln, i) => (
                      <tspan key={i} x={tcx} y={startY + i * lineH}>
                        {ln}
                      </tspan>
                    ))}
                    <title>{cleanLabel(n.label).replace(/\n/g, ' ')}</title>
                  </text>
                )
              })()}
            </g>
          )
        })}
      </svg>
    </div>
  )
}
